import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import BaseButton from "../../../components/ui/BaseButton";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { showToast } from "../../../store/toast/toastSlice";
import useAuth from "../../../hooks/useAuth";
// import { deleteClientAccount } from "../../../services/api/clientAccount";
import { setClientDashboardFilters } from "../../../store/filters/clientDashboardFilterSlice";
import { debounce, formatPhoneNumber } from "../../../utils/helpers";
import { ROLES } from "../../../utils/variables";
import { fetchAllClientAccounts } from "../../../services/api/client";
import { Avatar } from "@mui/material";

const ClientAccountTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getPermissionForModule, authRole } = useAuth();
  const { hasUpdateAccess, hasDeleteAccess, hasWriteAccess } = getPermissionForModule("CLIENT_CUSTOMER");

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const clientDashboardFilters = useSelector(
    (store) => store.clientDashboardFilters.workOrders
  );

  const queryParams = useMemo(() => ({
    page: clientDashboardFilters.pagination.page + 1,
    limit: clientDashboardFilters.pagination.pageSize,
    search: clientDashboardFilters.search || "",
  }), [clientDashboardFilters]);

  const { isLoading: isFetchingAccounts, data: accountsData, refetch: refetchAccounts } = useQuery({
    queryKey: ["clientAccounts", queryParams],
    queryFn: () => fetchAllClientAccounts(queryParams),
  });

  const [accountsList, setAccountsList] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  useEffect(() => {
    if (accountsData?.data?.accounts) {
      setAccountsList(accountsData.data?.accounts);
      setRowCount(accountsData.data.totalCount);
    }
  }, [accountsData]);

  const [menuOptions, setMenuOptions] = useState([]);
  useEffect(() => {
    const options = [];
    if (hasUpdateAccess) options.push({ label: "Edit", value: "EDIT" });
    if (hasDeleteAccess) options.push({ label: "Delete", value: "DELETE" });
    setMenuOptions(options);
  }, [hasUpdateAccess, hasDeleteAccess]);

  const debouncedSearchRef = useRef(
    debounce((value) => {
      dispatch(setClientDashboardFilters({ module: "workOrders", filterName: "search", data: value }));
    }, 500)
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    debouncedSearchRef.current(value);
  };

  const handleRemoveFilter = (key) => {
    dispatch(setClientDashboardFilters({ module: "workOrders", filterName: key, data: "" }));
    dispatch(setClientDashboardFilters({ module: "workOrders", filterName: "pagination", data: { page: 0, pageSize: clientDashboardFilters.pagination.pageSize } }));
    refetchAccounts();
  };

  const appliedFilters = useMemo(() => {
    const filters = [];
    const wf = clientDashboardFilters;
    if (wf.search) filters.push({ prefix: "Search", value: wf.search, key: "search" });
    return filters;
  }, [clientDashboardFilters]);

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <p
          // onContextMenu={(e) => handleContextMenu(e, params)} // Right-click triggers context menu
          className={`table-font-size font-normal cursor-pointer ${hasUpdateAccess ? "text-primary" : "text-gray-500"
            }`}
          onClick={() => {
            if (hasUpdateAccess) {
              navigate(`account/edit/${params?.id}`);
            }
          }}
        >
          {params?.value}
        </p>
      ),
    },
    {
      field: "accountsPerson",
      headerName: "Accounts Person",
      width: 200,
      renderCell: (params) => {
        return (
          <p className={`table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap cursor-pointer ${hasUpdateAccess ? "text-primary" : "text-gray-500"
            }`} onClick={() => {
              if (hasUpdateAccess) {
                navigate(`account/edit/${params?.row?.id}`);
              }
            }
            } >
            {params?.value || "-"}
          </p>
        );
      }
    },
    {
      field: "clientName",
      headerName: "Client Name",
      width: 200,
      renderCell: (params) => (
        <div className="flex items-center">
          <Avatar
            sx={{ width: 32, height: 32, marginRight: 1 }}
            src={params?.row?.clientLogo}
          />
          <p
            className={`table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap cursor-pointer ${hasUpdateAccess ? "text-primary" : "text-gray-500"
              }`}
          >
            {params?.row.client.clientName || "-"}
          </p>
        </div>
      ),
    },
    {
      field: "paymentTerms",
      width: 150,
      headerName: "Payment Terms",
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">
            {params?.value || "-"}
          </p>
        );
      },
    },
    {
      field: "taxType",
      width: 150,
      headerName: "Tax Type",
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">
            {params?.value || "-"}
          </p>
        );
      },
    },
    {
      field: "taxPercentage",
      width: 200,
      headerName: "Tax Percentage",
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">
            {params?.value || "-"}
          </p>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      renderCell: (params) => {
        return (
          <p className="table-font-size  cursor-pointer">
            {params?.value || "-"}
          </p>
        );
      },
    },

    {
      field: "phone",
      width: 200,
      headerName: "Contact",
      renderCell: (params) => {
        const value = params?.value;

        if (!value) return <p className="table-font-size">-</p>;

        const formatted = formatPhoneNumber(value);

        return (
          <p className="table-font-size cursor-pointer">
            {formatted}
          </p>
        );
      },
    },

    (hasUpdateAccess || hasDeleteAccess) && {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
          onItemClick={(value) => handleItemClick(value, params?.row)}
        />
      ),
      pinRight: true,
    }
  ].filter(Boolean);

  const handleItemClick = (value, row) => {
    if (value === "EDIT") {
      navigate(`account/edit/${row?.id}`);
    }
    if (value === "DELETE") {
      setSelectedAccount(row);
      setIsDeleteConfirmationModalOpen(true);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      // await deleteClientAccount(accountId);
      setIsDeleteConfirmationModalOpen(false);
      dispatch(showToast({ message: "Client account deleted successfully", type: "success" }));
      refetchAccounts();
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.message || "Error deleting account", type: "error" }));
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    dispatch(setClientDashboardFilters({
      module: "workOrders",
      filterName: "pagination",
      data: { page: newPaginationModel.page, pageSize: newPaginationModel.pageSize },
    }));
  };

  const handleNewAccount = () => {
    navigate("/workBoard/clients/account/create");
  };

  return (
    <>
      {isFetchingAccounts && <BaseLoader isLoading={isFetchingAccounts} />}
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md my-4 sticky t z-10 flex justify-between items-center">
        <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
          <p
            className="text-primary ml-2 cursor-pointer"
            onClick={() => setFiltersDrawerOpen(true)}
          >
            Add Filter
          </p>
        </div>
        <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
          <div className="flex gap-2">
            {appliedFilters.map((f) => (
              <div key={f.key + f.value} className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1">
                <span className="text-primary">{f.prefix}:</span>&nbsp;
                <p className="text-primary text-sm mx-1">{f.value}</p>
                <button onClick={() => handleRemoveFilter(f.key)} className="ml-2 text-primary hover:text-red-500">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>

        {authRole !== ROLES.FREELANCE && hasWriteAccess && (
          <BaseButton variant="contained" color="primary" style={{ padding: "6px 12px" }} onClick={handleNewAccount}>
            Create New Account
          </BaseButton>
        )}
      </div>

          <BaseDataTableV2
            className="max-h-[calc(100vh-300px)]"
            rowHeight={46}
            columns={columns}
            data={accountsList}
            isLoading={isFetchingAccounts}
            checkboxSelection
            disableColumnResize={false}
            disableRowSelectionOnClick={true}
            hideFooter={false}
            pagination
            paginationMode="server"
            paginationModel={clientDashboardFilters.pagination}
            onPaginationModelChange={handlePaginationChange}
            pageSizeOptions={[10, 20, 50, 100]}
            rowCount={rowCount}
          />

      {isDeleteConfirmationModalOpen && (
        <BaseConfirmModal
          open={isDeleteConfirmationModalOpen}
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          title="Delete Client Account"
          description={
            <p>
              Are you sure you want to delete the account of
              <span className="text-red-600 font-medium mx-1 capitalize">"{selectedAccount?.accountsPerson}"</span>?
              This action cannot be undone.
            </p>
          }
          onConfirm={() => handleDeleteAccount(selectedAccount?.id)}
        />
      )}
    </>
  );
};

export default ClientAccountTable;
